import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaTimes, FaCalendarAlt, FaClock, FaSearch } from 'react-icons/fa';
import { isAfter, parse, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import Banca, { BancaUtils, StatusBanca } from '../../../model/banca/Banca';
import { DataBancaViewModel } from '../../../viewmodel/banca/DataBancaViewModel';
import { useProcessoContext } from '../../../Routes/context/ProcessoUsuarioContext';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import CronogramaModal from './CronogramaModal';
import AnimatedModal from './AnimatedModal';
import ModalHelp from '../../../components/ModalHelp';

registerLocale('ptBR', ptBR);

interface DefinirDataModalProps {
  banca: Banca;
  onClose: () => void;
  onSaveSuccess: () => void;
}

const DefinirDataModal: React.FC<DefinirDataModalProps> = ({
  banca,
  onClose,
  onSaveSuccess
}) => {
  const [localDate, setLocalDate] = useState<Date | null>(null);
  const [localTime, setLocalTime] = useState<Date | null>(null);
  const [localEndTime, setLocalEndTime] = useState<Date | null>(null);
  const [dataExists, setDataExists] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { processo } = useProcessoContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: dateStr, horaInicial, horaFinal, isValid } = await DataBancaViewModel.getRawDataBanca(processo?.id!, banca.id);
        setDataExists(isValid);
        
        if (dateStr && horaInicial && horaFinal) {
          const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
          setLocalDate(parsedDate);
          
          const startDateTime = parse(`${dateStr} ${horaInicial}`, 'yyyy-MM-dd HH:mm:ss', new Date());
          setLocalTime(startDateTime);
          
          const endDateTime = parse(`${dateStr} ${horaFinal}`, 'yyyy-MM-dd HH:mm:ss', new Date());
          setLocalEndTime(endDateTime);
        }
      } catch (error) {
        console.error("Erro ao obter dados da banca", error);
      }
    };

    fetchData();
  }, [processo?.id, banca.id]);

  const handleConfirm = async () => {
    if (!localDate || !localTime || !localEndTime) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const combinedDateTime = new Date(localDate);
    combinedDateTime.setHours(localTime.getHours());
    combinedDateTime.setMinutes(localTime.getMinutes());

    const combinedEndDateTime = new Date(localDate);
    combinedEndDateTime.setHours(localEndTime.getHours());
    combinedEndDateTime.setMinutes(localEndTime.getMinutes());

    if (!isAfter(combinedDateTime, new Date())) {
      toast.error('A data selecionada deve ser futura');
      return;
    }

    if (!isAfter(combinedEndDateTime, combinedDateTime)) {
      toast.error('O horário de fim deve ser posterior ao horário de início');
      return;
    }

    if (BancaUtils.isStatusPosteriorOuIgual(StatusBanca.PLANEJADA, banca.status)) {
      const confirmed = window.confirm('Esta banca já possui status avançado. Deseja continuar?');
      if (!confirmed) return;
    }

    setLoading(true);
    try {
      const dateStr = combinedDateTime.toISOString().split('T')[0];
      const startTime = combinedDateTime.toTimeString().split(' ')[0];
      const endTime = combinedEndDateTime.toTimeString().split(' ')[0];

      let success = false;
      if (!dataExists) {
        success = await DataBancaViewModel.postDataBanca(processo?.id!, banca.id, dateStr, startTime, endTime);
      } else {
        success = await DataBancaViewModel.patchDataBanca(processo?.id!, banca.id, dateStr, startTime, endTime);
      }

      if (success) {
        toast.success('Data definida com sucesso!');
        onSaveSuccess();
        onClose();
      }
    } catch (error: any) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (showAdvanced) {
    return (
      <CronogramaModal
        banca={banca}
        processo={processo}
        onClose={() => setShowAdvanced(false)}
        onSaveSuccess={() => {
          onSaveSuccess();
          onClose();
        }}
      />
    );
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '700px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 30px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            Definir Data e Horário
            <FaCalendarAlt style={{ color: '#6c757d' }} />
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '30px' }}>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <p style={{ color: '#6c757d', margin: 0 }}>
              Selecione a data e os horários de início e fim da banca
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Data */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                marginBottom: '10px',
                fontWeight: '600',
                color: '#495057',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px'
              }}>
                <FaCalendarAlt /> Data
              </div>
              <DatePicker
                selected={localDate}
                onChange={(date) => setLocalDate(date)}
                inline
                dateFormat="dd/MM/yyyy"
                locale="ptBR"
              />
            </div>

            {/* Horário de Início */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                marginBottom: '10px',
                fontWeight: '600',
                color: '#28a745',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px'
              }}>
                <FaClock /> Início
              </div>
              <DatePicker
                selected={localTime}
                onChange={(time) => setLocalTime(time)}
                showTimeSelect
                showTimeSelectOnly
                timeFormat="HH:mm"
                timeIntervals={15}
                timeCaption="Início"
                inline
              />
            </div>

            {/* Horário de Fim */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                marginBottom: '10px',
                fontWeight: '600',
                color: '#dc3545',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px'
              }}>
                <FaClock /> Fim
              </div>
              <DatePicker
                selected={localEndTime}
                onChange={(time) => setLocalEndTime(time)}
                showTimeSelect
                showTimeSelectOnly
                timeFormat="HH:mm"
                timeIntervals={15}
                timeCaption="Fim"
                inline
              />
            </div>
          </div>

          {/* Resumo */}
          {localDate && localTime && localEndTime && (
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <strong>Resumo:</strong> {format(localDate, 'dd/MM/yyyy')} das {format(localTime, 'HH:mm')} às {format(localEndTime, 'HH:mm')}
            </div>
          )}

          {/* Botões */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setShowAdvanced(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: '1px solid #17a2b8',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <FaSearch /> Usar Cronograma de referência
            </button>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  background: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading || !localDate || !localTime || !localEndTime}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: '1px solid #28a745',
                  borderRadius: '4px',
                  cursor: loading || !localDate || !localTime || !localEndTime ? 'not-allowed' : 'pointer',
                  opacity: loading || !localDate || !localTime || !localEndTime ? 0.6 : 1
                }}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default DefinirDataModal;